import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../../PageUtil/PageContainer';
import PortfolioGallery from './BlogGallery.jsx';

import { togglePublished } from '../utilities/Portfolios/publishedPortfolioUtils';
import { useAuth } from '../../Auth/context/AuthContext';
import ConfirmationDialog from '../../Common/Feedback/ConfirmationDialog.jsx';
import useConfirmation from '../../Common/Feedback/useConfirmation.js';

const DEFAULT_ALLOWED_ACTIONS = ['Edit', 'Publish', 'View', 'Copy', 'CopyLink', 'Delete', 'Rename'];

const PortfolioListPage = ({
  type = "Draft",
  path,
  userId = null,
  loadPortfolio,
  deletePortfolio,
  loadPortfolios,
  portfolioExists,
  savePortfolio,
  renamePortfolio,
  allowedActions = DEFAULT_ALLOWED_ACTIONS,
  createAllowed = true,
  onSelectPortfolio,
  requiresUserId = true,
}) => {
  const { isAdmin } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [publishAcknowledged, setPublishAcknowledged] = useState(false);
  const [publishTargetTitle, setPublishTargetTitle] = useState('');
  const [publishing, setPublishing] = useState(false);
  const { requestConfirmation, confirmationProps } = useConfirmation();
  const navigate = useNavigate();

  const loadSinglePortfolio = async (uid, portfolioTitle) => {
    return loadPortfolio(uid, portfolioTitle);
  };

  const saveSinglePortfolio = async (uid, portfolioTitle, portfolioData) => {
    return savePortfolio(uid, portfolioTitle, portfolioData);
  };

  const renameSinglePortfolio = async (uid, oldTitle, newTitle) => {
    return renamePortfolio(uid, oldTitle, newTitle);
  };

  const portfolioAlreadyExists = async (uid, portfolioTitle) => {

    return portfolioExists(uid, portfolioTitle);
  };

  useEffect(() => {
    const fetchPortfolios = async () => {
      if (requiresUserId && !userId) {
        return;
      }

      try {
        const userPortfolios = await loadPortfolios(userId);
        setPortfolios(userPortfolios);
      } catch (err) {
        setError('We could not load your portfolios. Please try again.');
        console.error('Error loading portfolios:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolios();
  }, [loadPortfolios, userId, requiresUserId]);

  const handleDeletePortfolio = async (portfolioTitle) => {
    requestConfirmation({
      title: `Delete "${portfolioTitle}"?`,
      message: 'This portfolio will be permanently removed and cannot be restored.',
      confirmLabel: 'Delete portfolio',
      busyLabel: 'Deleting...',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deletePortfolio(userId, portfolioTitle);
          setPortfolios((current) => current.filter((portfolio) => portfolio.id !== portfolioTitle));
        } catch {
          setError('We could not delete that portfolio. Please try again.');
        }
      },
    });
  };

  const handleEditPortfolio = (portfolioTitle) => {
    const targetPath = `${path}/editor/${encodeURIComponent(portfolioTitle)}`;

    navigate(targetPath);
  };

  const handleViewPortfolio = (portfolioTitle) => {
    const targetPath = `${path}/view/${encodeURIComponent(portfolioTitle)}`;

    navigate(targetPath);
  };

  const handleTogglePublished = async (portfolioTitle) => {
    if (!userId) {
      setError('Please sign in to change publishing settings.');
      return;
    }

    const shouldPublish = type === 'Draft';

    if (shouldPublish) {
      setPublishTargetTitle(portfolioTitle);
      setPublishAcknowledged(false);
      setShowPublishConfirm(true);
      return;
    }

    try {
      setPublishing(true);
      const published = await togglePublished(userId, portfolioTitle);

      if (published) {
        navigate('/portfolios/published');
      } else {
        navigate('/portfolios/drafts');
      }
    } catch {
      setError(`We could not ${shouldPublish ? 'publish' : 'unpublish'} that portfolio. Please try again.`);
    } finally {
      setPublishing(false);
    }
  };

  const closePublishConfirm = () => {
    if (publishing) {
      return;
    }

    setShowPublishConfirm(false);
    setPublishAcknowledged(false);
    setPublishTargetTitle('');
  };

  const confirmPublishPortfolio = async () => {
    if (!publishTargetTitle || !publishAcknowledged) {
      return;
    }

    try {
      setPublishing(true);
      const published = await togglePublished(userId, publishTargetTitle);

      setShowPublishConfirm(false);
      setPublishAcknowledged(false);
      setPublishTargetTitle('');

      if (published) {
        navigate('/portfolios/published');
      } else {
        navigate('/portfolios/drafts');
      }
    } catch {
      setError('We could not publish that portfolio. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleCreatePortfolio = () => {
    const title = prompt('Enter portfolio title:');
    if (title && title.trim()) {
      const trimmedTitle = title.trim();
      const targetPath = `${path}/editor/${encodeURIComponent(trimmedTitle)}?new=true`;

      navigate(targetPath);
    }
  };

  const handleCopyPortfolio = async (portfolioTitle) => {
    const suggestedTitle = `${portfolioTitle} Copy`;
    const newTitlePrompt = 'Enter title for the duplicate portfolio:';
    const newTitle = prompt(newTitlePrompt, suggestedTitle);

    if (!newTitle || !newTitle.trim()) {
      return;
    }

    const trimmedTitle = newTitle.trim();
    const effectiveUserId = userId;

    try {
      if (requiresUserId && !effectiveUserId) {
        setError('Please sign in before copying a portfolio.');
        return;
      }

      const alreadyExists = await portfolioAlreadyExists(effectiveUserId, trimmedTitle);
      if (alreadyExists) {

        setError(`A ${type.toLowerCase()} Portfolio named "${trimmedTitle}" already exists.`);
        return;
      }

      const sourcePortfolio = await loadSinglePortfolio(effectiveUserId, portfolioTitle);
      if (!sourcePortfolio) {
        setError('The portfolio you tried to copy could not be found.');
        return;
      }

      const { id, title, createdAt, updatedAt, ...portfolioData } = sourcePortfolio;
      await saveSinglePortfolio(effectiveUserId, trimmedTitle, portfolioData);

      const refreshedPortfolios = await loadPortfolios(effectiveUserId);
      setPortfolios(refreshedPortfolios);
    } catch {
      setError('We could not copy that portfolio. Please try again.');
    }
  };

  const handleCopyPortfolioLink = async (portfolioTitle) => {
    if (typeof window === 'undefined') {
      setError('Links cannot be copied on this device or browser.');
      return;
    }

    const targetPath = `${path}/view/${encodeURIComponent(portfolioTitle)}`;
    const absoluteUrl = `${window.location.origin}${targetPath}`;

    try {
      await navigator.clipboard.writeText(absoluteUrl);
    } catch {
      setError('We could not copy the link. You can copy it from your browser address bar instead.');
      return;
    }
  };

  const handleRenamePortfolio = async (portfolioTitle) => {
    if (!renamePortfolio) {
      setError('This portfolio cannot be renamed here.');
      return;
    }

    const newTitlePrompt = 'Enter new title for this portfolio:';
    const proposedTitle = prompt(newTitlePrompt, portfolioTitle);

    if (!proposedTitle || !proposedTitle.trim()) {
      return;
    }

    const trimmedTitle = proposedTitle.trim();
    const effectiveUserId = userId;

    if (trimmedTitle === portfolioTitle) {
      return;
    }

    try {
      if (requiresUserId && !effectiveUserId) {
        setError('Please sign in before renaming a portfolio.');
        return;
      }

      const alreadyExists = await portfolioAlreadyExists(effectiveUserId, trimmedTitle);
      if (alreadyExists) {
        setError(`A ${type.toLowerCase()} Portfolio named "${trimmedTitle}" already exists.`);
        return;
      }

      await renameSinglePortfolio(effectiveUserId, portfolioTitle, trimmedTitle);
      const refreshedPortfolios = await loadPortfolios(effectiveUserId);
      setPortfolios(refreshedPortfolios);
    } catch {
      setError('We could not rename that portfolio. Please try again.');
    }
  };

  return (
    <PageContainer error={error} setError={setError} isAdmin={isAdmin}>
      <div className="py-5">
        {/* Header Section */}
        <div className="mb-5 px-3 px-md-0">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h1 className="h2 mb-1 fw-bold">
                <i className="bi bi-journals me-2 color-primary"></i>
                My {type} Portfolios
              </h1>
              <p className="text-muted mb-0">
                {portfolios.length} portfolio{portfolios.length !== 1 ? 's' : ''} created
              </p>
            </div>
            {createAllowed ? (
              <button
                className="btn btn-lg btn-success rounded-2"
                onClick={handleCreatePortfolio}
              >
                <i className="bi bi-plus-lg me-2"></i>
                Create
              </button>
            ) : null}
          </div>
        </div>

        <PortfolioGallery
          portfolios={portfolios}
          loading={loading}
          type={type}
          allowedActions={allowedActions}
          createAllowed={createAllowed}
          onCreatePortfolio={handleCreatePortfolio}
          onEdit={handleEditPortfolio}
          onTogglePublished={handleTogglePublished}
          onView={handleViewPortfolio}
          onCopy={handleCopyPortfolio}
          onCopyLink={handleCopyPortfolioLink}
          onDelete={handleDeletePortfolio}
          onRename={handleRenamePortfolio}
          onSelect={onSelectPortfolio}
        />
      </div>

      {showPublishConfirm && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)', zIndex: 1080 }}
        >
          <div className="card border-0 shadow-lg mx-3" style={{ maxWidth: '700px', width: '100%' }}>
            <div className="card-body p-4 p-md-5">
              <h2 className="h4 fw-bold mb-3">Confirm Public Publication</h2>
              <p className="mb-3 text-muted">
                You are about to publish <strong>{publishTargetTitle}</strong>. Once published, this portfolio will be publicly accessible and may be viewable by anyone with the link.
              </p>

              <div className="alert alert-warning" role="alert">
                <h3 className="h6 fw-bold mb-2">Publication Disclaimer</h3>
                <p className="mb-2">
                  By selecting Publish, you represent and warrant that you have all rights, permissions, and lawful authority required to make this content publicly available.
                </p>
                <p className="mb-2">
                  You acknowledge that publication may expose personal, confidential, or proprietary information and that public content may be copied, redistributed, cached, archived, or indexed by third parties, including search engines.
                </p>
                <p className="mb-0">
                  You remain solely responsible for compliance with applicable laws, contractual obligations, privacy requirements, and intellectual property rights. Removing this portfolio later may not fully remove previously distributed or cached copies.
                </p>
              </div>

              <div className="form-check mb-4">
                <input
                  id="publish-legal-ack"
                  className="form-check-input"
                  type="checkbox"
                  checked={publishAcknowledged}
                  onChange={(event) => setPublishAcknowledged(event.target.checked)}
                  disabled={publishing}
                />
                <label className="form-check-label" htmlFor="publish-legal-ack">
                  I have read and understand the publication disclaimer and consent to make this portfolio public.
                </label>
              </div>

              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closePublishConfirm}
                  disabled={publishing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmPublishPortfolio}
                  disabled={!publishAcknowledged || publishing}
                >
                  {publishing ? 'Publishing...' : 'Publish Portfolio'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmationDialog {...confirmationProps} />
    </PageContainer>
  );
};
export default PortfolioListPage;
